import React, { useEffect, useState, useCallback } from "react";
import { Box, Autocomplete, TextField, Checkbox, FormControlLabel } from "@mui/material";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ConfigureForm from "../../../common/customComponents/ConfigureForm";
import ConfigTable from "../../../common/customComponents/ConfigTable";
import { panelStyle } from "../../../common/customStyles/CustomStyles";
import { Toaster } from "../../../common/alertComponets/Toaster";
import { GetAllProjects } from "../../../service/api/ProjectService";
import { GetAllTasks } from "../../../service/api/TaskService";
import {
    SaveProjectTaskConfig,
    GetAllProjectTaskConfigs,
    DeleteProjectTaskConfig
} from "../../../service/api/ProjectTaskConfigService";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function ProjectTaskConfig() {
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [configData, setConfigData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(5);
    const [totalElements, setTotalElements] = useState(0);

    const fetchInitialData = async () => {
        try {
            const [projectsRes, tasksRes] = await Promise.all([
                GetAllProjects(),
                GetAllTasks()
            ]);
            setProjects(projectsRes);
            setTasks(tasksRes);
        } catch (error) {
            Toaster("error", "Failed to fetch projects or tasks.");
        }
    };

    const fetchConfigs = useCallback(async () => {
        try {
            const res = await GetAllProjectTaskConfigs(page, size);
            setConfigData(res.content || []);
            setTotalElements(res.totalElements || 0);
        } catch (error) {
            Toaster("error", "Failed to fetch configurations.");
        }
    }, [page, size]);

    useEffect(() => {
        fetchInitialData();
        setHeaders(["Project Name", "Project Code", "Tasks", "Action"]);
    }, []);

    useEffect(() => {
        fetchConfigs();
    }, [fetchConfigs]);

    const handleSave = async () => {
        if (!selectedProject) {
            Toaster("warning", "Please select a project.");
            return;
        }
        if (selectedTasks.length === 0) {
            Toaster("warning", "Please select at least one task.");
            return;
        }

        const payload = {
            projectCode: selectedProject.projectCode,
            taskMasterIds: selectedTasks.map(t => t.taskMasterId)
        };

        try {
            await SaveProjectTaskConfig(payload);
            Toaster("success", "Project task mapping saved successfully.");
            setSelectedProject(null);
            setSelectedTasks([]);
            fetchConfigs();
        } catch (error) {
            Toaster("error", "Failed to save configuration.");
        }
    };

    const handleDelete = async (item) => {
        try {
            await DeleteProjectTaskConfig(item.projectCode);
            Toaster("success", "Project task mapping deleted successfully.");
            fetchConfigs();
        } catch (error) {
            Toaster("error", "Failed to delete configuration.");
        }
    };

    const handleEdit = (item) => {
        const project = projects.find(p => p.projectCode === item.projectCode);
        setSelectedProject(project || null);

        const taskIds = item.taskMasterIds || (item.taskMasterId ? [item.taskMasterId] : []);
        const selected = tasks.filter(t => taskIds.includes(t.taskMasterId));
        setSelectedTasks(selected);

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const tableContent = useCallback(() => {
        // Consolidate rows by projectCode and map IDs to descriptions
        const groupedData = configData.reduce((acc, current) => {
            let existing = acc.find(item => item.projectCode === current.projectCode);
            const currentTaskIds = current.taskMasters
                ? current.taskMasters.map(tm => tm.taskMasterId)
                : (current.taskMasterId ? [current.taskMasterId] : []);

            if (existing) {
                currentTaskIds.forEach(id => {
                    if (!existing.taskMasterIds.includes(id)) {
                        existing.taskMasterIds.push(id);
                    }
                });
            } else {
                acc.push({
                    ...current,
                    taskMasterIds: [...currentTaskIds]
                });
            }
            return acc;
        }, []);

        return {
            actions: {
                edit: true,
                delete: true,
                view: false,
            },
            content: groupedData.map((config) => {
                const project = projects.find(p => p.projectCode === config.projectCode);
                const taskDescriptions = config.taskMasterIds.map(id => {
                    const task = tasks.find(t => t.taskMasterId === id);
                    return task ? task.taskDescription : id;
                }).filter(desc => desc).join(", ");

                return [
                    { isPrint: true, forAction: false, value: project ? project.projectName : config.projectCode },
                    { isPrint: true, forAction: false, value: config.projectCode },
                    {
                        isPrint: true,
                        forAction: false,
                        value: taskDescriptions
                    },
                    {
                        forAction: true,
                        isPrint: false,
                        value: config,
                    },
                ];
            }),
        };
    }, [configData, projects, tasks]);

    const actionClick = (event, item) => {
        if (event === "delete") {
            handleDelete(item);
        } else if (event === "edit") {
            handleEdit(item);
        }
    };

    return (
        <Box component="div" sx={{ ...panelStyle, padding: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <h3 style={{ marginTop: 0 }}>Project Task Configuration</h3>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Autocomplete
                        id="project-select"
                        options={projects}
                        getOptionLabel={(option) => `${option.projectName} (${option.projectCode})`}
                        value={selectedProject}
                        onChange={(event, newValue) => setSelectedProject(newValue)}
                        sx={{ width: 300 }}
                        renderInput={(params) => <TextField {...params} label="Select Project" variant="outlined" size="small" />}
                    />

                    <Autocomplete
                        multiple
                        id="tasks-select"
                        options={tasks}
                        disableCloseOnSelect
                        getOptionLabel={(option) => option.taskDescription || ""}
                        value={selectedTasks}
                        onChange={(event, newValue) => setSelectedTasks(newValue)}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {option.taskDescription}
                            </li>
                        )}
                        sx={{ width: 400 }}
                        renderInput={(params) => (
                            <TextField {...params} label="Select Tasks" placeholder="Tasks" variant="outlined" size="small" />
                        )}
                    />

                    <button
                        className="btn btn-primary"
                        style={{ height: '40px', backgroundColor: '#412276', color: 'white', border: 'none', borderRadius: '4px', padding: '0 20px', cursor: 'pointer' }}
                        onClick={handleSave}
                    >
                        Save
                    </button>
                </Box>
            </Box>

            <ConfigTable
                data={tableContent()}
                headers={headers}
                actions={actionClick}
            />
        </Box>
    );
}

export default ProjectTaskConfig;
